import { useEffect, useMemo } from 'react';
import { createInfiniteQuery } from 'react-query-kit';
import { keepPreviousData } from '@tanstack/react-query';
import { debounce } from 'es-toolkit';

import { logger } from '@zep/core-logger';

import type { CursorSearchResponseListSearchQuizsetRes } from '@/api/gen/_model/cursorSearchResponseListSearchQuizsetRes';
import type { QuizsetCursorSearchReq } from '@/api/gen/_model/quizsetCursorSearchReq';
import { searchQuizsetsByCursor as searchQuizsetsByCursorApi } from '@/api/gen/quiz/quiz.gen';
import {
  type GetAllQuizesPayload,
  usePublicQuizListPayload,
} from '@/module/public/hooks/useGetAllQuizesPayload';

type QuizsetCursorSearchVariables = Omit<QuizsetCursorSearchReq, 'cursor'>;

const PUBLIC_SEARCH_CACHE_STALE_TIME_MS = 5 * 60 * 1000;
const MAX_AUTOMATIC_REFETCH_PAGES = 1;
const MAX_EMPTY_CURSOR_CONTINUATIONS = 3;

type PublicQuizsetSearchPage = CursorSearchResponseListSearchQuizsetRes;

const searchQuizsetsByCursor = (
  quizsetCursorSearchReq: QuizsetCursorSearchReq,
  signal?: AbortSignal,
) => searchQuizsetsByCursorApi(quizsetCursorSearchReq, undefined, signal);

const searchQuizsetsByCursorSkippingEmptyContinuations = async (
  quizsetCursorSearchReq: QuizsetCursorSearchReq,
  signal?: AbortSignal,
) => {
  let response = await searchQuizsetsByCursor(quizsetCursorSearchReq, signal);
  const initialTotalCount = response.totalCount;

  for (
    let skippedCount = 0;
    skippedCount < MAX_EMPTY_CURSOR_CONTINUATIONS;
    skippedCount += 1
  ) {
    if (response.data.length > 0 || !response.hasNext || !response.nextCursor) {
      break;
    }

    response = await searchQuizsetsByCursor(
      {
        ...quizsetCursorSearchReq,
        cursor: response.nextCursor,
      },
      signal,
    );
  }

  return {
    ...response,
    totalCount: response.totalCount ?? initialTotalCount,
  };
};

const shouldAutomaticallyRefetchPublicSearch = (query: {
  state: { data?: unknown };
}) => {
  const data = query.state.data as { pages?: unknown[] } | undefined;
  return (data?.pages?.length ?? 0) <= MAX_AUTOMATIC_REFETCH_PAGES;
};

const logEvent = ({
  categoryFilters,
  subjectFilter,
  totalQuizCount,
  order,
  period,
}: Omit<GetAllQuizesPayload, 'enabled'> & { totalQuizCount?: number }) => {
  logger.track('filter_search', {
    ...categoryFilters,
    ...subjectFilter,
    templateType: categoryFilters.templateType ?? 'all',
    ...(totalQuizCount != null && { totalQuizCount }),
    order,
    period,
  });
};

const sharedDebouncedLogger = debounce(logEvent, 1000);

export const useGetAllQuizes = () => {
  const { enabled, categoryFilters, subjectFilter, order, period } =
    usePublicQuizListPayload();

  const result = useFetchAllQuizes({
    enabled,
    variables: {
      categoryFilters: {
        criteria: categoryFilters,
        ...subjectFilter,
      },
      order,
      period,
    },
  });

  const flattenedData = useMemo(() => {
    return result.data?.pages.flatMap(page => page.data ?? []) ?? [];
  }, [result.data]);

  useEffect(() => {
    if (result.data?.pages.length === 1) {
      const firstPage = result.data.pages[0];
      sharedDebouncedLogger({
        categoryFilters,
        subjectFilter,
        order,
        period,
        totalQuizCount: firstPage.totalCount ?? undefined,
      });
    }
  }, [categoryFilters, subjectFilter, order, period, result.data?.pages]);

  return {
    ...result,
    isLoading: !enabled ? true : result.isLoading,
    flatData: flattenedData,
    hasNextPage: result.hasNextPage,
    fetchNextPage: result.fetchNextPage,
    isFetchingNextPage: result.isFetchingNextPage,
  };
};

export const useFetchAllQuizes = createInfiniteQuery<
  PublicQuizsetSearchPage,
  QuizsetCursorSearchVariables,
  Error,
  string | undefined
>({
  queryKey: ['quizsets/search'],
  fetcher: async (variables, { pageParam, signal }) => {
    return searchQuizsetsByCursorSkippingEmptyContinuations(
      {
        categoryFilters: variables.categoryFilters,
        order: variables.order,
        period: variables.period,
        cursor: pageParam,
      },
      signal,
    );
  },
  staleTime: PUBLIC_SEARCH_CACHE_STALE_TIME_MS,
  placeholderData: keepPreviousData,
  refetchOnMount: shouldAutomaticallyRefetchPublicSearch,
  refetchOnWindowFocus: shouldAutomaticallyRefetchPublicSearch,
  getNextPageParam: lastPage => {
    return lastPage.hasNext ? lastPage.nextCursor ?? undefined : undefined;
  },
  initialPageParam: undefined,
});
